import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  UserCircle,
  Building2,
  Save,
  Plus,
  Trash2,
  Users,
  Crown,
  Mail,
  Coins,
  Calendar,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import HUDPageHeader from '../components/HUDPageHeader';
import HUDStatCard from '../components/HUDStatCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileTypes, setProfileTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [companyForm, setCompanyForm] = useState({ name: '', siret: '', address: '' });
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState('member');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const [profileRes, typesRes] = await Promise.all([
        axios.get(`${API}/profile`, { withCredentials: true }),
        axios.get(`${API}/profile-types`, { withCredentials: true })
      ]);
      setProfile(profileRes.data);
      setProfileTypes(typesRes.data);
      if (profileRes.data.company) {
        fetchCompany(profileRes.data.company_id);
      }
    } catch (error) {
      toast.error('Erreur de chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompany = async (companyId) => {
    try {
      const res = await axios.get(`${API}/companies/${companyId}`, { withCredentials: true });
      setCompany(res.data);
    } catch (error) {
      setCompany(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/profile`, {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        address: profile.address,
        profile_type: profile.profile_type,
        company_type: profile.company_type,
        siret: profile.siret
      }, { withCredentials: true });
      setProfile(res.data);
      updateUser(res.data);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCompany = async () => {
    try {
      const res = await axios.post(`${API}/companies`, companyForm, { withCredentials: true });
      setCompany(res.data);
      setShowCompanyDialog(false);
      fetchProfile();
      toast.success('Société créée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleAddMember = async () => {
    if (!company) return;
    try {
      await axios.post(`${API}/companies/${company.company_id}/members`, {
        email: memberEmail,
        role: memberRole
      }, { withCredentials: true });
      toast.success('Membre ajouté');
      setShowAddMemberDialog(false);
      setMemberEmail('');
      fetchCompany(company.company_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!company) return;
    try {
      await axios.delete(`${API}/companies/${company.company_id}/members/${memberId}`, { withCredentials: true });
      toast.success('Membre retiré');
      fetchCompany(company.company_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
    : '—';
  const accountStatus = profile?.status === 'active' ? 'Actif'
    : profile?.status === 'pending' ? 'En attente'
    : profile?.status === 'banned' ? 'Banni'
    : '—';
  const accountStatusAccent = profile?.status === 'active' ? '#10B981'
    : profile?.status === 'pending' ? '#F59E0B'
    : '#EF4444';

  return (
    <div data-hud-page data-testid="profile-page" className="fade-in">
      <HUDPageHeader
        module="Cockpit"
        breadcrumb={[{ label: 'Hub', to: '/studio/dashboard' }, { label: 'Profil' }]}
        title="Mon Profil"
        subtitle="Gérez vos informations personnelles, professionnelles et les paramètres de votre compte."
        icon={UserCircle}
      />

      <div className="max-w-5xl px-4 sm:px-6 lg:px-8 py-6">
      {/* Stats top-row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8" data-testid="profile-stats-row">
        <HUDStatCard
          testid="stat-profile-tokens"
          icon={Coins}
          label="Jetons"
          value={user?.tokens || 0}
          hint="Disponibles"
          accent="#D9A05B"
        />
        <HUDStatCard
          testid="stat-profile-status"
          icon={Shield}
          label="Compte"
          value={accountStatus}
          hint={user?.is_admin ? 'Rôle admin' : 'Rôle membre'}
          accent={accountStatusAccent}
        />
        <HUDStatCard
          testid="stat-profile-member-since"
          icon={Calendar}
          label="Membre depuis"
          value={memberSince}
          hint="Date d'inscription"
          accent="#A855F7"
        />
        <HUDStatCard
          testid="stat-profile-company"
          icon={Building2}
          label="Société"
          value={company?.name ? (company.name.length > 12 ? company.name.slice(0, 11) + '…' : company.name) : '—'}
          hint={company?.members_count ? `${company.members_count} membre${company.members_count > 1 ? 's' : ''}` : 'Non renseignée'}
          accent="#10B981"
        />
      </div>

      {/* Status badge */}
      {profile?.status === 'pending' && (
        <div data-testid="pending-banner" className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
          <div className="w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
          <p className="text-amber-800 text-sm font-medium">Votre compte est en attente de validation par un administrateur.</p>
        </div>
      )}

      <div className="grid gap-6">
        {/* Personal Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-blue-600" />
            Informations personnelles
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Prénom</label>
              <Input
                data-testid="profile-first-name"
                value={profile?.first_name || ''}
                onChange={(e) => setProfile({...profile, first_name: e.target.value})}
                placeholder="Votre prénom"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Nom</label>
              <Input
                data-testid="profile-last-name"
                value={profile?.last_name || ''}
                onChange={(e) => setProfile({...profile, last_name: e.target.value})}
                placeholder="Votre nom"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Email</label>
              <Input value={profile?.email || ''} disabled className="bg-gray-50" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Téléphone</label>
              <Input
                data-testid="profile-phone"
                value={profile?.phone || ''}
                onChange={(e) => setProfile({...profile, phone: e.target.value})}
                placeholder="+33 6 00 00 00 00"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Adresse</label>
              <Input
                data-testid="profile-address"
                value={profile?.address || ''}
                onChange={(e) => setProfile({...profile, address: e.target.value})}
                placeholder="Adresse postale"
              />
            </div>
          </div>
        </div>

        {/* Professional Info */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Informations professionnelles
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Type de profil</label>
              <Select
                value={profile?.profile_type || 'none'}
                onValueChange={(v) => setProfile({...profile, profile_type: v === 'none' ? null : v})}
              >
                <SelectTrigger data-testid="profile-type-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non défini</SelectItem>
                  {profileTypes.map(t => (
                    <SelectItem key={t.type_id} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Statut</label>
              <Select
                value={profile?.company_type || 'none'}
                onValueChange={(v) => setProfile({...profile, company_type: v === 'none' ? null : v})}
              >
                <SelectTrigger data-testid="profile-company-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non défini</SelectItem>
                  <SelectItem value="societe">Société</SelectItem>
                  <SelectItem value="independant">Indépendant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">SIRET</label>
              <Input
                data-testid="profile-siret"
                value={profile?.siret || ''}
                onChange={(e) => setProfile({...profile, siret: e.target.value})}
                placeholder="N° SIRET"
              />
            </div>
          </div>
        </div>

        {/* Company Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium text-gray-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Société
            </h2>
            {!company && (
              <Button size="sm" onClick={() => setShowCompanyDialog(true)} data-testid="create-company-btn">
                <Plus className="w-4 h-4 mr-1" />
                Créer une société
              </Button>
            )}
          </div>

          {company ? (
            <div>
              <div className="p-4 bg-gray-50 rounded-lg mb-4">
                <p className="font-medium text-gray-900">{company.name}</p>
                {company.siret && <p className="text-sm text-gray-500">SIRET: {company.siret}</p>}
                {company.address && <p className="text-sm text-gray-500">{company.address}</p>}
              </div>

              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Membres ({company.members?.length || 0})</h3>
                {profile?.is_company_admin && (
                  <Button size="sm" variant="outline" onClick={() => setShowAddMemberDialog(true)} data-testid="add-member-btn">
                    <Plus className="w-3 h-3 mr-1" />
                    Ajouter
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                {(company.members || []).map(member => (
                  <div key={member.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-sm font-medium">{member.name?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{member.name}</p>
                        <p className="text-xs text-gray-500">{member.email}</p>
                      </div>
                      {member.role === 'admin' && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium flex items-center gap-1">
                          <Crown className="w-3 h-3" /> Admin
                        </span>
                      )}
                    </div>
                    {profile?.is_company_admin && member.user_id !== company.admin_user_id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:bg-red-50 h-8 w-8"
                        onClick={() => handleRemoveMember(member.user_id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Vous n'appartenez à aucune société. Créez-en une ou attendez une invitation.</p>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-profile-btn"
            className="bg-blue-600 hover:bg-blue-700 px-8"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      {/* Create Company Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer une société</DialogTitle>
            <DialogDescription>Les membres pourront partager les projets et tokens.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Nom de la société *</label>
              <Input value={companyForm.name} onChange={(e) => setCompanyForm({...companyForm, name: e.target.value})} placeholder="Ma Société SAS" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">SIRET</label>
              <Input value={companyForm.siret} onChange={(e) => setCompanyForm({...companyForm, siret: e.target.value})} placeholder="N° SIRET" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Adresse</label>
              <Input value={companyForm.address} onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})} placeholder="Adresse du siège" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompanyDialog(false)}>Annuler</Button>
            <Button onClick={handleCreateCompany} disabled={!companyForm.name} className="bg-blue-600 hover:bg-blue-700">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={showAddMemberDialog} onOpenChange={setShowAddMemberDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un membre</DialogTitle>
            <DialogDescription>L'utilisateur doit avoir un compte Shoebox3D existant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Email du membre *</label>
              <Input
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
                placeholder="email@exemple.com"
                type="email"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1.5">Rôle</label>
              <Select value={memberRole} onValueChange={setMemberRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Membre</SelectItem>
                  <SelectItem value="admin">Admin société</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMemberDialog(false)}>Annuler</Button>
            <Button onClick={handleAddMember} disabled={!memberEmail} className="bg-blue-600 hover:bg-blue-700">
              <Mail className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default ProfilePage;
